// ===================================
// HEADER COMPONENT - src/components/Header.tsx
// ===================================
import React, { useState } from 'react';
import {
  User,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  LayoutDashboard,
  MessageCircle,
  Search,
  File
} from "lucide-react";
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Box, Drawer, IconButton } from '@mui/material';
import SideMenu from './SideMenu';

interface HeaderProps {
  title: string;
  subtitle?: string;
  showNavigation?: boolean;
}

const Header: React.FC<HeaderProps> = ({ title, subtitle, showNavigation = true }) => {
  const { user, logout } = useAuth();
  const { notifications } = useSettings();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: <LayoutDashboard className='w-4'/> },
    { name: "Chat", href: "/chat", icon: <MessageCircle className='w-4'/> },
    { name: "Search", href: "/search", icon: <Search className='w-4'/> },
    { name: "Documents", href: "/documents", icon: <File className='w-4'/> },
  ];

  return (
    <>
      <header className="bg-[#020617] shadow-sm border-b border-[#334155]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Left side - Title and Navigation */}
            <div className="flex items-center space-x-8">
              <div className="flex items-center gap-4">
                {/* Hamburger Icon */}
                <IconButton
                  onClick={() => setIsSidebarOpen(true)}
                  className="!text-[#64748b] hover:text-gray-300"
                  size="large"
                >
                  <Menu />
                </IconButton>

                <div>
                  <h1 className="text-2xl font-bold text-white">
                    {user?.current_business}
                  </h1>
                  {subtitle && (
                    <p className="text-[13px] text-[#64748b]">
                      Powered By MicroAgent Chatbot
                    </p>
                  )}
                </div>
              </div>

              {/* Desktop Navigation */}
              {showNavigation && (
                <nav className="hidden md:flex space-x-1">
                  {navigation.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-white hover:text-white hover:bg-[#1e293b] transition-colors"
                    >
                      <span>{item.icon}</span>
                      <span>{item.name}</span>
                    </a>
                  ))}
                </nav>
              )}
            </div>

            {/* Right side - User menu */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              {notifications && (
                <button className="relative p-2 text-[#64748b] hover:text-white focus:outline-none rounded-full">
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400"></span>
                </button>
              )}

              {/* User info and menu */}
              <div className="flex items-center space-x-3">
                <div className="hidden sm:block text-right">
                  <div className="text-sm font-medium text-white">
                    {user?.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {user?.role_name} • {user?.current_business}
                  </div>
                </div>

                {/* User menu dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 p-2 rounded-full "
                  >
                    <div className="w-8 h-8 bg-[#8b3dff] rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-[#1e293b] rounded-md shadow-lg py-1 z-50 border border-[#334155]">
                      <a
                        href="/profile"
                        className="flex items-center px-4 py-2 text-sm text-[#64748b] hover:bg-[#020617] cursor-pointer"
                      >
                        <User className="w-4 h-4 mr-3" />
                        Profile
                      </a>
                      <a
                        href="/settings"
                        className="flex items-center px-4 py-2 text-sm text-[#64748b] hover:bg-[#020617] border-b border-[#334155] cursor-pointer"
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        Settings
                      </a>

                      <button
                        onClick={logout}
                        className="flex items-center w-full px-4 py-2 text-sm text-[#64748b] hover:bg-[#020617] cursor-pointer"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile menu button */}
                {showNavigation && (
                  <button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    className="md:hidden p-2 rounded-md text-[#64748b] hover:text-white hover:bg-[#020617] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {showMobileMenu ? (
                      <X className="w-6 h-6" />
                    ) : (
                      <Menu className="w-6 h-6" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          {showMobileMenu && showNavigation && (
            <div className="md:hidden border-t border-[#334155] py-4">
              <nav className="space-y-1">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-white hover:bg-[#020617]"
                  >
                    <span className="text-lg">{item.icon}</span>
                    <span>{item.name}</span>
                  </a>
                ))}
              </nav>
            </div>
          )}
        </div>
      </header>

      <Drawer
        anchor="left"
        open={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        PaperProps={{
          sx: { backgroundColor: "#0f172a", color: "white", width: 260 },
        }}
      >
        <Box role="presentation" onClick={() => setIsSidebarOpen(false)}>
          <SideMenu />
        </Box>
      </Drawer>
    </>
  );
};

export default Header;